import { useRouteError, isRouteErrorResponse, Link, useNavigate } from 'react-router';
import MascotLight from '@gearghost/brand-assets/mascot/light.png';
import { createLogger } from '@/utils/logger';
import styles from './RouteErrorBoundary.module.scss';

const logger = createLogger('RouteErrorBoundary');

/**
 * RouteErrorBoundary - Error UI for route-level errors.
 * Uses React Router's useRouteError hook to get error details.
 * Handles HTTP errors (404, 401, etc) and JavaScript errors.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  // Retry by revalidating loaders (navigates to same route)
  const handleRetry = () => {
    navigate(0);
  };

  // Check if it's an HTTP response error (from loader throwing Response)
  if (isRouteErrorResponse(error)) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <img
            src={MascotLight}
            alt="GearGhost"
            className={styles.ghost}
          />
          <div className={styles.statusCode}>{error.status}</div>
          <h1 className={styles.title}>{getStatusTitle(error.status)}</h1>
          <p className={styles.message}>
            {getStatusMessage(error.status, error.data)}
          </p>
          <div className={styles.buttonGroup}>
            <button className={styles.primaryButton} onClick={handleRetry}>
              Try Again
            </button>
            <Link to="/" className={styles.secondaryButton}>
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Check if it's a chunk loading error
  const isChunkError =
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Failed to fetch') ||
    error?.name === 'ChunkLoadError';

  if (isChunkError) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <img
            src={MascotLight}
            alt="GearGhost"
            className={styles.ghost}
          />
          <h1 className={styles.title}>Failed to Load Page</h1>
          <p className={styles.message}>
            The page couldn&apos;t load. This might be due to a network issue or a recent update.
          </p>
          <div className={styles.buttonGroup}>
            <button
              className={styles.primaryButton}
              onClick={() => window.location.reload()}
            >
              Reload Page
            </button>
            <Link to="/" className={styles.secondaryButton}>
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Generic JavaScript error
  logger.error({ err: error }, 'Unhandled route error');

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <img
          src={MascotLight}
          alt="GearGhost"
          className={styles.ghost}
        />
        <h1 className={styles.title}>Something Went Wrong</h1>
        <p className={styles.message}>
          An unexpected error occurred. Please try again.
        </p>
        {import.meta.env.DEV && error?.message && (
          <pre className={styles.errorBox}>
            {error.message}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        )}
        <div className={styles.buttonGroup}>
          <button className={styles.primaryButton} onClick={handleRetry}>
            Try Again
          </button>
          <Link to="/" className={styles.secondaryButton}>
            Go Home
          </Link>
        </div>
      </div>
    </div>
  );
}

function getStatusTitle(status) {
  switch (status) {
    case 400:
      return 'Bad Request';
    case 401:
      return 'Hold Up!';
    case 403:
      return 'Off Limits';
    case 404:
      return 'Page Not Found';
    case 500:
      return 'We Hit a Snag';
    default:
      return 'Oops!';
  }
}

function getStatusMessage(status, errorData) {
  // Show custom error data in dev mode (e.g., simulated errors)
  if (import.meta.env.DEV && errorData && typeof errorData === 'string') {
    return errorData;
  }

  switch (status) {
    case 400:
      return "Something's not quite right with that request. Double-check and try again.";
    case 401:
      return "Looks like you need to log in first. Only ghosts allowed in here!";
    case 403:
      return "This area is vendor-only. If you think you should have access, reach out to support.";
    case 404:
      return "This page has ghosted us. It might have moved on or never existed.";
    case 500:
      return "Our servers are having a moment. Give it another shot, or try again in a bit.";
    default:
      return "Something unexpected happened. If this keeps up, let us know!";
  }
}

export default RouteErrorBoundary;
